import { ArrowRight, Plus } from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

import { appInfo } from "@/lib/app-info";
import { fileStore } from "@/lib/fileStore";
import { useCurrentUser, useFileUrl, useGlobeMarkers, useStoreMatchingItems } from "@/lib/hooks";
import { Guardian, typeDefs } from "@/lib/schema";
import { store } from "@/lib/store";
import { GlobeMarker } from "@/lib/types-util";

function useIsSignedIn(): boolean | null {
    const user = useCurrentUser();
    if (user === null) return null; // loading
    return user !== undefined;
}

function HeroSection() {
    const isSignedIn = useIsSignedIn();

    return (
        <section className="section-gap stack-gap">
            <div className="glass p-8 rounded-major shadow-glass stack-gap">
                <h1 className="hero-headline text-5xl sm:text-6xl jade tracking-tight">
                    Protect What You Love
                </h1>
                <p className="hero-subtle text-lg text-[rgba(247,244,239,0.8)] max-w-3xl">
                    Guardians Collective empowers people to tell their stories and rally support
                    for projects protecting places, species, and communities—set against a living globe.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                    {isSignedIn ? (
                        <Link
                            href="/create-guardian"
                            className="btn btn--primary inline-flex items-center gap-2"
                            aria-label="Add your Guardian"
                        >
                            <Plus className="size-4" aria-hidden="true" />
                            <span>Add your Guardian</span>
                        </Link>
                    ) : (
                        <Link
                            href="/recommended-guardian"
                            className="btn btn--primary inline-flex items-center gap-2"
                            aria-label="Recommend a Guardian"
                        >
                            <Plus className="size-4" aria-hidden="true" />
                            <span>Recommend a Guardian</span>
                        </Link>
                    )}
                    {/* <Link
                        href="/guardians"
                        className="btn btn--secondary inline-flex items-center gap-2"
                        aria-label="Explore the impact map"
                    >
                        <Globe2 className="size-4" aria-hidden="true" />
                        <span>Explore the Impact Map</span>
                    </Link> */}
                </div>
                <div className="pt-2 text-sm text-[rgba(247,244,239,0.6)]">
                    Stories. Projects. A shared mission—brought into focus.
                </div>
            </div>
        </section>
    );
}

function GuardianImage({ guardian }: { guardian: Guardian }) {
    const url = useFileUrl(guardian.image_path);
    if (url === null) {
        return <div className="aspect-[16/10] w-full rounded-minor bg-[rgba(255,255,255,0.06)] animate-pulse" />;
    }
    if (!url) {
        return <div className="aspect-[16/10] w-full rounded-minor bg-[rgba(255,255,255,0.06)]" />;
    }
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={url} alt={guardian.name} className="aspect-[16/10] w-full rounded-minor object-cover" />;
}

function TagPills({ tags }: { tags: string[] | undefined }) {
    if (!tags || tags.length === 0) return null;
    const show = tags.slice(0, 3);
    return (
        <div className="flex flex-wrap gap-2 pt-1">
            {show.map((t, i) => (
                <span key={`${t}-${i}`} className="tag-pill">
                    {t}
                </span>
            ))}
        </div>
    );
}

function GuardianCard({ guardian }: { guardian: Guardian }) {
    const desc = guardian.description || "Guardian protecting what they love.";
    return (
        <Link
            href={`/guardian/${guardian.id}`}
            className="glass card-actionable p-4 rounded-major shadow-glass stack-gap focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(0,196,122,0.6)]"
            aria-label={`Open guardian ${guardian.name}`}
        >
            <GuardianImage guardian={guardian} />
            <div className="flex items-center justify-between">
                <h3 className="gc-heading text-xl jade">{guardian.name}</h3>
                <span className="text-subtle text-xs">Approved</span>
            </div>
            <p className="text-[rgba(247,244,239,0.75)] text-sm line-clamp-2">{desc}</p>
            <TagPills tags={guardian.tags} />
        </Link>
    );
}

function CardPlaceholder({ index }: { index: number }) {
    return (
        <div
            className="glass p-4 rounded-major shadow-glass stack-gap"
            aria-hidden="true"
            key={`ph-${index}`}
        >
            <div className="aspect-[16/10] w-full rounded-minor bg-[rgba(255,255,255,0.06)] animate-pulse" />
            <div className="h-5 w-2/3 rounded bg-[rgba(255,255,255,0.06)] animate-pulse" />
            <div className="h-4 w-full rounded bg-[rgba(255,255,255,0.06)] animate-pulse" />
            <div className="flex gap-2 pt-1">
                <div className="h-6 w-16 rounded-full bg-[rgba(255,255,255,0.06)] animate-pulse" />
                <div className="h-6 w-14 rounded-full bg-[rgba(255,255,255,0.06)] animate-pulse" />
                <div className="h-6 w-12 rounded-full bg-[rgba(255,255,255,0.06)] animate-pulse" />
            </div>
        </div>
    );
}

function FeaturedGuardiansSection() {
    const featured = useStoreMatchingItems<Guardian>(
        typeDefs.Guardian,
        {
            is_featured: true as any,
            status: "approved" as any,
            is_public: true as any,
        } as Partial<Guardian>,
        { orderBy: "created_at", orderByDesc: true, limit: 6 }
    );

    const [markers, setMarkers] = useState<GlobeMarker[] | null>(null);
    useGlobeMarkers(markers);
    useEffect(() => {
        if (!featured?.length) {
            return;
        }
        let m = true;
        Promise.all(featured.map(f => store().selectMatchesAsync(typeDefs.Project, { guardian_id: f.id }))).then(v => {
            const all: GlobeMarker[] = [];
            for (const p of v) {
                all.push(
                    ...p.map(v => ({
                        lat: v.lat ?? 0,
                        lng: v.lng ?? 0,
                        template: "pin",
                        imageUrl: v.image_path ? fileStore().getUrlSync(v.image_path) : undefined,
                    }))
                );
            }
            if (m) {
                setMarkers(all);
            }
        });
        return () => {
            m = false;
        };
    }, [featured]);

    const hasLoaded = featured !== null;
    const items = featured || [];

    const content = useMemo(() => {
        if (!hasLoaded) {
            return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <CardPlaceholder key={i} index={i} />
                    ))}
                </div>
            );
        }

        if (items.length === 0) {
            return (
                <div className="glass p-6 rounded-major shadow-glass flex items-center justify-between gap-4">
                    <div className="stack-gap">
                        <h3 className="gc-heading text-xl">Featured Guardians</h3>
                        <p className="text-[rgba(247,244,239,0.75)]">
                            No featured Guardians yet. Be the first to share your story.
                        </p>
                    </div>
                    <Link href="/register" className="btn btn--primary inline-flex items-center gap-2">
                        <Plus className="size-4" />
                        <span>Get started</span>
                    </Link>
                </div>
            );
        }

        return (
            <>
                {/* Horizontal scroll on small screens */}
                <div className="flex gap-4 overflow-x-auto pb-1 sm:hidden">
                    {items.map(g => (
                        <div key={g.id} className="min-w-72 max-w-80">
                            <GuardianCard guardian={g} />
                        </div>
                    ))}
                </div>
                {/* Responsive grid on larger screens */}
                <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(g => (
                        <GuardianCard key={g.id} guardian={g} />
                    ))}
                </div>
            </>
        );
    }, [hasLoaded, items]);

    return (
        <section className="section-gap stack-gap">
            <div className="flex items-center justify-between">
                <h2 className="gc-heading text-2xl jade">Featured Guardians</h2>
                <Link href="/guardians" className="inline-flex items-center gap-2 text-[rgba(247,244,239,0.85)] hover:jade transition">
                    <span className="text-sm">View all</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
            </div>
            {content}
        </section>
    );
}

export default function LandingPage() {
    return (
        <div className="page--LandingPage stack-gap">
            {appInfo.comingSoon?
                (
                    <div className="page--LandingPage stack-gap">
                    <h1 className="text-xl glass mt-20 self-start p-8">Coming Soon</h1>
                </div>
                ):
            <>
                <HeroSection />
                <FeaturedGuardiansSection />
            </>}
        </div>
    );
}